/**
 * Alpine.js component for product variant selection and section rendering swaps.
 */

export const findMatchingVariant = (variants, selectedOptions) => {
  if (!variants || !variants.length || !selectedOptions || !selectedOptions.length) {
    return null
  }
  return (
    variants.find((variant) =>
      variant.options.every((opt, idx) => opt === selectedOptions[idx])
    ) || null
  )
}

/**
 * Checks if an option value at a given position is available in stock
 * matching all preceding selected options.
 *
 * @param {Array} variants List of variant objects
 * @param {Array} selectedOptions Array of currently selected options [opt1, opt2, opt3]
 * @param {Number} positionIndex 0-indexed option position (0 = option 1, 1 = option 2, etc.)
 * @param {String} optionValue The option value to check (e.g. "Small", "Black")
 * @returns {Boolean}
 */
export const isOptionAvailable = (variants, selectedOptions, positionIndex, optionValue) => {
  if (!variants || !variants.length) return false

  return variants.some((variant) => {
    if (variant.options[positionIndex] !== optionValue) return false
    if (!variant.available) return false

    // Ensure all preceding option selections match
    for (let i = 0; i < positionIndex; i++) {
      if (selectedOptions[i] && variant.options[i] !== selectedOptions[i]) {
        return false
      }
    }

    return true
  })
}

/**
 * Checks if a combination exists at all (regardless of inventory).
 */
export const isOptionExisting = (variants, selectedOptions, positionIndex, optionValue) => {
  if (!variants || !variants.length) return false

  return variants.some((variant) => {
    if (variant.options[positionIndex] !== optionValue) return false

    for (let i = 0; i < positionIndex; i++) {
      if (selectedOptions[i] && variant.options[i] !== selectedOptions[i]) {
        return false
      }
    }

    return true
  })
}

export default {
  name: 'productVariantPicker',
  component(config = {}) {
    return {
      variants: [],
      currentVariant: null,
      selectedOptions: [],
      pickerType: 'radio',
      loading: false,
      sectionId: '',
      productUrl: '',

      init() {
        this.pickerType = this.$el.dataset.prodify || config.pickerType || 'radio'
        this.sectionId = this.$el.dataset.section || config.sectionId || ''
        this.productUrl = this.$el.dataset.url || config.productUrl || window.location.pathname

        const scriptEl = this.$el.querySelector('[data-prodify-variants-json]')
        if (scriptEl) {
          try {
            this.variants = JSON.parse(scriptEl.textContent)
          } catch (e) {
            console.error('Failed to parse product variants JSON', e)
          }
        } else if (config.variants) {
          this.variants = config.variants
        }

        this.updateCurrentOptions()
        this.currentVariant =
          findMatchingVariant(this.variants, this.selectedOptions) || this.variants[0] || null

        this.$el.addEventListener('change', this.onVariantChange.bind(this))
      },

      isOptionAvailable(positionIndex, optionValue) {
        return isOptionAvailable(this.variants, this.selectedOptions, positionIndex, optionValue)
      },

      isOptionExisting(positionIndex, optionValue) {
        return isOptionExisting(this.variants, this.selectedOptions, positionIndex, optionValue)
      },

      updateCurrentOptions() {
        if (this.pickerType === 'select') {
          this.selectedOptions = Array.from(
            this.$el.querySelectorAll('select[data-option]'),
            (select) => select.value
          )
          return
        }

        const optionContainers = Array.from(
          this.$el.querySelectorAll('[data-prodify-option-container]')
        )
        this.selectedOptions = optionContainers.map((container) => {
          const checked = Array.from(container.querySelectorAll('input')).find(
            (radio) => radio.checked
          )
          const val = checked?.value || ''
          const selectedValEl = container.querySelector('#selected-value')
          if (selectedValEl) selectedValEl.textContent = val
          return val
        })
      },

      onVariantChange(event) {
        if (event.target.hasAttribute('data-prodify-ignore')) return

        this.updateCurrentOptions()

        const changingFirstOption =
          event.target.dataset?.optionPosition === '1' && this.selectedOptions.length > 1

        if (changingFirstOption) {
          this.currentVariant =
            this.variants.find((v) => v.options && v.options[0] === this.selectedOptions[0]) || null
        } else {
          this.currentVariant = findMatchingVariant(this.variants, this.selectedOptions)
        }

        this.updateAvailabilityClasses()

        if (this.currentVariant) {
          this.updateURL()
          this.updateVariantInputs()
          this.swapSection()
        }
      },

      updateAvailabilityClasses() {
        if (!this.variants.length) return

        const optionContainers = Array.from(
          this.$el.querySelectorAll('[data-prodify-option-container]')
        )

        optionContainers.forEach((container, positionIndex) => {
          if (positionIndex === 0) return
          const inputs = Array.from(container.querySelectorAll('input[type="radio"], option'))

          inputs.forEach((input) => {
            const val = input.getAttribute('value')
            const available = this.isOptionAvailable(positionIndex, val)
            const exists = this.isOptionExisting(positionIndex, val)

            if (available) {
              input.classList.remove('disabled')
              if (this.pickerType === 'select') {
                input.innerText = val
              }
            } else {
              input.classList.add('disabled')
              if (this.pickerType === 'select') {
                const soldOutText = window.variantStrings?.soldout_with_option || '[value] - Sold out'
                const unavailableText =
                  window.variantStrings?.unavailable_with_option || '[value] - Unavailable'
                input.innerText = exists
                  ? soldOutText.replace('[value]', val)
                  : unavailableText.replace('[value]', val)
              }
            }
          })
        })
      },

      updateURL() {
        if (!this.currentVariant || this.$el.dataset.updateUrl === 'false') return
        const newUrl = `${this.productUrl}?variant=${this.currentVariant.id}`
        window.history.replaceState({}, '', newUrl)

        const returnTo = document.querySelector("input[type='hidden'][name='return_to']")
        if (returnTo) {
          returnTo.value = returnTo.value.replace(/variant=(\d+)/, `variant=${this.currentVariant.id}`)
        }
      },

      updateVariantInputs() {
        if (!this.currentVariant) return
        const inputs = this.$el.querySelectorAll(
          'input[data-prodify-variant-input], input[name="id"], input[name="id[]"]'
        )
        inputs.forEach((input) => {
          if (!input.classList.contains('moonshaft-addon-checkbox')) {
            input.value = this.currentVariant.id
          }
        })
      },

      async swapSection() {
        if (!this.currentVariant || !this.sectionId) return
        this.loading = true

        const swappableSelectors = [
          '[data-prodify-price-container]',
          '[data-prodify-media-container]',
          '[data-prodify-options]',
          '[data-prodify-addons-container]',
          '[data-prodify-details-container]',
        ]

        const targets = swappableSelectors
          .map((sel) => this.$el.querySelector(sel))
          .filter(Boolean)

        targets.forEach((target) => {
          target.classList.add('transition-opacity', 'duration-200', 'opacity-50')
        })

        try {
          const endpoint = `${this.productUrl}?variant=${this.currentVariant.id}&section_id=${this.sectionId}`
          const response = await fetch(endpoint)
          const responseText = await response.text()
          const doc = new DOMParser().parseFromString(responseText, 'text/html')

          const performSwap = () => {
            // Declarative data-prodify-swap attribute elements
            const swapElements = doc.querySelectorAll('[data-prodify-swap]')
            if (swapElements.length > 0) {
              swapElements.forEach((src) => {
                const key = src.dataset.prodifySwap
                const tgt = this.$el.querySelector(`[data-prodify-swap="${key}"]`)
                if (tgt && src) tgt.replaceWith(src)
              })
            }

            // Fallback legacy selector targets
            const selectors = [
              '[data-prodify-price-container]',
              '[data-prodify-media-container]',
              '[data-prodify-options]',
              '[data-prodify-addons-container]',
              '[name="add"]',
              '#shipping-on',
              '#bis-form',
              '#buy-now',
              '[data-prodify-details-container]',
            ]

            selectors.forEach((sel) => {
              const src = doc.querySelector(sel)
              const tgt = this.$el.querySelector(sel)
              if (src && tgt) {
                tgt.replaceWith(src)
                if (window.Alpine) {
                  window.Alpine.initTree(src)
                }
              }
            })

            if (window.Shopify?.PaymentButton?.init) {
              window.Shopify.PaymentButton.init()
            }

            window.dispatchEvent(new Event('change', { bubbles: true }))
          }

          if (document.startViewTransition) {
            document.startViewTransition(() => performSwap())
          } else {
            performSwap()
          }

          window.dispatchEvent(
            new CustomEvent('moonshaft:variant:changed', {
              detail: { variant: this.currentVariant },
              bubbles: true,
            })
          )
        } catch (e) {
          console.error('Error swapping section info for variant:', e)
        } finally {
          this.loading = false
          targets.forEach((target) => {
            target.classList.remove('opacity-50')
          })
        }
      },
    }
  },
}
